import * as React from "react";
import { Field, StatusPill } from "@ozpanel/kit";
import { Button, ConfirmDialog, useAuth, useToast } from "@ozpanel/ui";

/**
 * Runs a list of operations. ONE implementation, used by the per-module tab and by the per-domain
 * grouping alike.
 *
 * It is split out because the alternative is two copies, and every defect worth reporting this
 * week came from two copies of one thing drifting: served_here read in one branch, delegation
 * graded in one of two tables, a label describing a field it no longer matched. A second copy of
 * the runner would have been the next one.
 */

export type OpParam = {
  name: string;
  in: "path" | "query" | "body";
  required: boolean;
  type: string;
  description?: string;
};

export type Operation = {
  method: string;
  path: string;
  summary: string;
  detail?: string;
  danger: string;
  min_role: string;
  params: OpParam[];
  documented: boolean;
};

const dangerTone = (d: string) => (d === "high" ? "bad" : d === "medium" ? "warn" : "neutral");

export function OperationRunner({ operations }: { operations: Operation[] }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [running, setRunning] = React.useState<{ op: Operation; values: Record<string, string> } | null>(null);
  const [result, setResult] = React.useState<string | null>(null);
  // A typed acknowledgement for high-danger operations. This surface is DERIVED, so it cannot know
  // an operation's preconditions the way a purpose-built screen can: it would have offered
  // "Initialise the cluster CA" on a node that already holds one. It cannot detect that, so it
  // must not imply it has.
  const [ack, setAck] = React.useState("");

  const submit = async () => {
    const r = running;
    setRunning(null);
    if (!r) return;
    // Path parameters are substituted into the URL; query parameters appended; body fields sent as
    // JSON. An empty optional field is OMITTED rather than sent as "" — an empty string is a value,
    // and for a field like `subnet` it means "set it to nothing", not "leave it alone".
    let path = r.op.path;
    const query: string[] = [];
    const body: Record<string, unknown> = {};
    for (const p of r.op.params) {
      const v = (r.values[p.name] ?? "").trim();
      if (p.in === "path") {
        path = path.replace(`{${p.name}}`, encodeURIComponent(v));
      } else if (!v) {
        continue;
      } else if (p.in === "query") {
        query.push(`${encodeURIComponent(p.name)}=${encodeURIComponent(v)}`);
      } else if (p.type === "boolean") {
        body[p.name] = v === "true" || v === "yes" || v === "1";
      } else if (p.type === "integer" || p.type === "number") {
        body[p.name] = Number(v);
      } else if (p.type === "array") {
        body[p.name] = v.split(",").map((s) => s.trim()).filter(Boolean);
      } else {
        body[p.name] = v;
      }
    }
    const url = path.replace(/^\/api\/v1/, "") + (query.length ? `?${query.join("&")}` : "");
    try {
      const res = await client.apiFetch<unknown>(url, {
        method: r.op.method,
        ...(Object.keys(body).length ? { body: JSON.stringify(body) } : {}),
      });
      // The RESPONSE is shown, not a toast saying "done". These are operations whose whole value is
      // what they report back — which node was promoted, what was skipped, what conflicted — and a
      // green tick discards exactly the part that mattered.
      setResult(JSON.stringify(res, null, 2));
      notify({ kind: "success", title: `${r.op.method} ${url}` });
    } catch (e) {
      notify({
        kind: "error",
        title: `${r.op.method} ${url} failed`,
        body: e instanceof Error ? e.message : String(e),
      });
    }
  };

  if (operations.length === 0) return null;

  return (
    <>
      <div className="divide-y divide-line rounded-lg border border-line">
        {operations.map((op) => (
          <div key={`${op.method} ${op.path}`} className="flex items-start justify-between gap-3 px-3 py-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] text-muted">{op.method}</span>
                <span className="font-mono text-[12px]">{op.path.replace(/^\/api\/v1/, "")}</span>
                {op.danger && op.danger !== "low" && (
                  <StatusPill tone={dangerTone(op.danger)}>{op.danger} risk</StatusPill>
                )}
                {/* Shown only when the shape is genuinely unreadable. It used to fire for every
                    write with no schema, which included 159 bare actions that need no input at
                    all — a warning on the correct case is one an operator learns to ignore. */}
                {!op.documented && <StatusPill tone="warn">request body not documented</StatusPill>}
              </div>
              <div className="mt-0.5 text-[12px]">{op.summary || <em>no summary declared</em>}</div>
            </div>
            <Button
              size="sm"
              variant={op.danger === "high" ? "danger" : "ghost"}
              onClick={() => {
                setAck("");
                setRunning({ op, values: {} });
              }}
            >
              Run
            </Button>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!running}
        onOpenChange={(o) => !o && setRunning(null)}
        title={running ? `${running.op.method} ${running.op.path.replace(/^\/api\/v1/, "")}` : ""}
        confirmLabel="Run it"
        destructive={running?.op.danger === "high"}
        confirmDisabled={running?.op.danger === "high" && ack.trim().toUpperCase() !== "RUN"}
        body={
          <div className="space-y-2">
            {running?.op.summary && <p className="text-[12.5px]">{running.op.summary}</p>}
            {running?.op.danger === "high" && (
              <p className="rounded border border-bad/40 bg-bad/10 px-2 py-1.5 text-[12px] leading-relaxed">
                This list is built from the API documentation, so it does NOT check whether this
                operation applies to this node right now — a purpose-built screen for this module
                may, and if one exists it is the safer place to do this. Type{" "}
                <span className="font-mono font-semibold">RUN</span> to confirm you have checked.
              </p>
            )}
            {running?.op.danger === "high" && (
              <Field mono label="Type RUN to confirm" value={ack} placeholder="RUN" onChange={setAck} />
            )}
            {running?.op.params.length === 0 && (
              <p className="text-[12px] text-muted">This operation takes no parameters.</p>
            )}
            {running?.op.params.map((p) => (
              <Field
                key={`${p.in}:${p.name}`}
                mono
                label={`${p.name}${p.required ? "" : " (optional)"}`}
                value={running.values[p.name] ?? ""}
                placeholder={p.type === "array" ? "comma-separated" : p.type}
                help={p.description || `${p.in} parameter, ${p.type}`}
                onChange={(v) => setRunning({ ...running, values: { ...running.values, [p.name]: v } })}
              />
            ))}
            {running && !running.op.documented && (
              <p className="rounded border border-warn/40 bg-warn/10 px-2 py-1.5 text-[12px]">
                This route accepts a request body its documentation does not describe, so no fields
                can be shown for it. It will be sent with only the parameters above — which may not
                be enough for it to succeed.
              </p>
            )}
          </div>
        }
        onConfirm={() => void submit()}
      />

      <ConfirmDialog
        open={!!result}
        onOpenChange={(o) => !o && setResult(null)}
        title="Result"
        confirmLabel="Close"
        body={
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded border border-line bg-bg p-2 font-mono text-[11px]">
            {result}
          </pre>
        }
        onConfirm={() => setResult(null)}
      />
    </>
  );
}
